// src/pages/Extension.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./resume-styles.css";

import HeroSection from "../components/ui/HeroSection";
import ResumeUpload from "../components/ui/ResumeUpload";
import ResumeForm from "../components/ui/ResumeForm";
import SuccessNotification from "../components/ui/SuccessNotification";
import PDFViewer from "../components/ui/PDFViewer";
import AuthRequiredModal from "../components/ui/AuthRequiredModal";
import { emptyResumeData } from "../utils/resumeData";
import { saveResumeProfile } from "../utils/resumeProfileAPI";

// ✅ NEW: uploaded resume endpoints helpers
import {
    uploadResumePdf,
    getResumeMetadata,
    downloadResumeUrl,
} from "../utils/resumeUploadAPI";

function Extension() {
    const navigate = useNavigate();
    const [resumeData, setResumeData] = useState({ ...emptyResumeData });
    const [showSuccess, setShowSuccess] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfFileName, setPdfFileName] = useState("");
    const [extensionId, setExtensionId] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // ✅ NEW: to handle presigned URL expiration + upload-after-login
    const [pdfExpiresAt, setPdfExpiresAt] = useState(null);
    const [pendingPdfFile, setPendingPdfFile] = useState(null);

    const refreshPdfFromBackend = async (email) => {
        const dl = await downloadResumeUrl(email);
        if (dl.success && dl.hasResume) {
            // If we had a blob URL, revoke it
            if (pdfUrl && String(pdfUrl).startsWith("blob:")) {
                URL.revokeObjectURL(pdfUrl);
            }

            setPdfUrl(dl.downloadUrl);
            setPdfFileName(dl.fileName || "");
            setPdfExpiresAt(Date.now() + dl.expiresIn * 1000);
            setShowForm(true); // ✅ ensures PDF preview shows on Extension tab automatically
        }
    };

    useEffect(() => {
        const loggedIn = localStorage.getItem("isLoggedIn") === "true";
        setIsLoggedIn(loggedIn);

        const userEmail = localStorage.getItem("userEmail");

        // Load profile data from localStorage fallback (your existing behavior)
        if (loggedIn) {
            const storageKey = userEmail
                ? `jobque_resume_data_${userEmail}`
                : "jobque_resume_data";
            const savedData = localStorage.getItem(storageKey);

            if (savedData) {
                try {
                    const parsed = JSON.parse(savedData);
                    setResumeData(parsed);
                    setShowForm(true); // ✅ if they have saved data, show form automatically
                } catch (error) {
                    console.error("Error loading saved resume data:", error);
                }
            }
        }

        // ✅ NEW: If logged in, also check uploaded resume existence and auto-load preview
        if (loggedIn && userEmail) {
            (async () => {
                const meta = await getResumeMetadata(userEmail);
                if (meta.success && meta.hasResume) {
                    await refreshPdfFromBackend(userEmail);
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        document.body.classList.add("resume-extension-body");
        return () => {
            document.body.classList.remove("resume-extension-body");
        };
    }, []);

    const getExtensionId = () =>
        extensionId ||
        window.__RESUME_AUTOFILL_EXTENSION_ID__ ||
        window.localStorage.getItem("resumeAutofillExtensionId");

    useEffect(() => {
        let mounted = true;
        let gotId = false;

        const handleMessage = (event) => {
            if (!mounted || event.source !== window) return;
            const d = event.data;
            if (
                d &&
                d.type === "RESUME_AUTOFILL_EXTENSION_ID" &&
                typeof d.extensionId === "string"
            ) {
                gotId = true;
                setExtensionId(d.extensionId);
                window.__RESUME_AUTOFILL_EXTENSION_ID__ = d.extensionId;
                window.localStorage.setItem("resumeAutofillExtensionId", d.extensionId);
            }
        };

        window.addEventListener("message", handleMessage);

        const pre =
            window.__RESUME_AUTOFILL_EXTENSION_ID__ ||
            window.localStorage.getItem("resumeAutofillExtensionId");
        if (pre && typeof pre === "string") {
            gotId = true;
            setExtensionId(pre);
        }

        const ping = () => {
            window.postMessage({ type: "RESUME_AUTOFILL_PING" }, "*");
        };

        ping();
        const pingTimer = window.setInterval(() => {
            if (!gotId) ping();
        }, 750);

        return () => {
            mounted = false;
            window.clearInterval(pingTimer);
            window.removeEventListener("message", handleMessage);
        };
    }, []);

    // ✅ NEW: refresh download URL shortly before it expires (5 min URLs)
    useEffect(() => {
        const email = localStorage.getItem("userEmail");
        if (!isLoggedIn || !email || !pdfExpiresAt) return;

        // refresh 10 seconds before expiry
        const msUntilRefresh = Math.max(pdfExpiresAt - Date.now() - 10000, 5000);
        const t = setTimeout(() => refreshPdfFromBackend(email), msUntilRefresh);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdfExpiresAt, isLoggedIn]);

    const handleFileSelect = async (file) => {
        setShowForm(true);

        // show instant local preview
        if (pdfUrl && String(pdfUrl).startsWith("blob:")) URL.revokeObjectURL(pdfUrl);
        const url = URL.createObjectURL(file);
        setPdfUrl(url);
        setPdfFileName(file.name);
        setPdfExpiresAt(null); // local blob doesn’t expire

        const email = localStorage.getItem("userEmail");

        // not logged in → upload later
        if (!isLoggedIn || !email) {
            setPendingPdfFile(file);
            return;
        }

        // logged in → upload now, then swap preview to presigned URL
        const up = await uploadResumePdf(email, file);
        if (up.success) {
            await refreshPdfFromBackend(email);
        } else {
            console.error("Resume upload failed:", up.message);
            alert(up.message || "Failed to upload resume PDF.");
        }
    };

    const handleGetStarted = () => {
        setShowForm(true);
        setTimeout(() => {
            const formElement = document.getElementById("resume-form");
            if (formElement) {
                formElement.scrollIntoView({ behavior: "smooth" });
            }
        }, 100);
    };

    const handleSave = async () => {
        if (!isLoggedIn) {
            localStorage.setItem("temp_resume_data", JSON.stringify(resumeData));
            setShowAuthModal(true);
            return;
        }

        setIsSaving(true);

        try {
            const fullName = `${resumeData.firstName || ""} ${resumeData.lastName || ""}`.trim();
            const updatedResumeData = { ...resumeData, fullName };

            const userEmail = localStorage.getItem("userEmail");

            const result = await saveResumeProfile(userEmail, updatedResumeData);
            if (!result.success) throw new Error(result.message);

            if (userEmail) {
                const storageKey = `jobque_resume_data_${userEmail}`;
                localStorage.setItem(storageKey, JSON.stringify(updatedResumeData));
            }
            localStorage.setItem("jobque_resume_data", JSON.stringify(updatedResumeData));

            window.postMessage({ type: "RESUME_AUTOFILL_SAVE", payload: updatedResumeData }, "*");

            const onResult = (e) => {
                if (e.source !== window) return;
                if (e.data && e.data.type === "RESUME_AUTOFILL_SAVE_RESULT") {
                    window.removeEventListener("message", onResult);
                }
            };
            window.addEventListener("message", onResult);

            setShowSuccess(true);

            setTimeout(() => {
                navigate("/profile?tab=resume");
            }, 1500);
        } catch (error) {
            console.error("Error saving data:", error);
            alert("Failed to save resume data. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    // ✅ NEW: if user logs in after selecting a PDF, upload it now
    useEffect(() => {
        if (!isLoggedIn) return;

        const email = localStorage.getItem("userEmail");
        if (!email) return;

        (async () => {
            // restore temp form data (your existing behavior)
            const tempData = localStorage.getItem("temp_resume_data");
            if (tempData) {
                try {
                    const parsed = JSON.parse(tempData);
                    setResumeData(parsed);
                    setShowForm(true);
                    localStorage.removeItem("temp_resume_data");

                    setTimeout(() => {
                        const formElement = document.getElementById("resume-form");
                        if (formElement) {
                            formElement.scrollIntoView({ behavior: "smooth" });
                        }
                    }, 500);
                } catch (error) {
                    console.error("Error loading temp resume data:", error);
                }
            }

            // upload pending PDF if it exists
            if (pendingPdfFile) {
                const up = await uploadResumePdf(email, pendingPdfFile);
                if (up.success) {
                    await refreshPdfFromBackend(email);
                } else {
                    console.error("Pending resume upload failed:", up.message);
                }
                setPendingPdfFile(null);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    return (
        <div className="resume-extension-page min-h-screen py-12 px-4">
            {showSuccess && (
                <SuccessNotification
                    message="Resume profile saved successfully! Redirecting to your profile..."
                    onClose={() => setShowSuccess(false)}
                />
            )}

            <AuthRequiredModal show={showAuthModal} onClose={() => setShowAuthModal(false)} />

            <div className="mx-auto w-full max-w-none">
                <HeroSection />

                {showForm && (
                    <div id="resume-form" className="mt-16">
                        <div className="text-center mb-8 animate-fade-in-up">
                            <h2 className="text-3xl md:text-4xl font-bold text-[var(--forest-green)] mb-4 font-poppins">
                                Your Resume Profile
                            </h2>
                            <p className="text-lg text-[#4B5563]">
                                Fill out your information once and never type it again
                            </p>
                        </div>

                        <div className="flex flex-col lg:flex-row gap-8 items-start relative">
                            <PDFViewer
                                pdfUrl={pdfUrl}
                                fileName={pdfFileName}
                                scrollTargetId="resume-form"
                                headerOffset={120}
                            />

                            <div className="w-full lg:w-1/2 xl:w-1/2 2xl:w-1/2 lg:ml-auto">
                                <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8">
                                    <ResumeUpload onFileSelect={handleFileSelect} />
                                    <ResumeForm
                                        data={resumeData}
                                        onChange={setResumeData}
                                        onSave={handleSave}
                                        isSaving={isSaving}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {!showForm && (
                    <div className="text-center mt-12">
                        <button
                            onClick={handleGetStarted}
                            className="primary-cta-button px-10 py-5 bg-[var(--forest-green)] text-white font-bold text-xl rounded-full hover:bg-[var(--deep-teal)] transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5"
                        >
                            Create Your Resume Profile Now
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Extension;